import React, { useState } from "react";
import styles from "../FlotteTable/FlotteTable.module.scss";
import EditButton from "./EditButton";
import {
  mouvementOptions,
  getEditableColumns,
  fieldConfigs,
  formatDisplayValue,
} from "./simulatorUtils";
import useFlotteSimulator from "./useFlotteSimulator";
import FlotteRmeTable from "./FlotteRmeTable";

const SIMULATION_PRORATA_INFO = `La ligne « Simulation km 🔁 » est créée uniquement s'il y a un kilométrage récupéré. Elle est mise à jour aussi à chaque récupération de kilométrage.
Toutes les autres valeurs dépendantes du kilométrage sont recalculées sur la ligne de projection.
Si le véhicule n'a aucun km connu, elle est inexistante.`;

const mouvementBorderColors = {
  contrat: "#2E7D32",
  prolongation: "#C9A227",
  fin_reelle: "#E07A1F",
  simulation: "#909090",
  simulation_prorata_km: "#909090",
};

const SimulationProrataLabel = ({ name }) => {
  const [popoverPos, setPopoverPos] = useState(null);
  const labelRef = React.useRef(null);

  const handleClick = () => {
    if (popoverPos) {
      setPopoverPos(null);
      return;
    }
    const rect = labelRef.current?.getBoundingClientRect();
    if (rect) {
      setPopoverPos({ top: rect.top, left: rect.right + 8 });
    }
  };

  return (
    <span style={{ display: "inline-block" }}>
      <span
        ref={labelRef}
        style={{ cursor: "pointer", textDecoration: "underline dotted" }}
        onClick={handleClick}
      >
        {name}
      </span>
      {popoverPos && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 999,
            }}
            onClick={() => setPopoverPos(null)}
          />
          <div
            style={{
              position: "fixed",
              top: popoverPos.top,
              left: popoverPos.left,
              zIndex: 1000,
              background: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: 6,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              padding: "10px 14px",
              minWidth: 280,
              maxWidth: 340,
              fontSize: 13,
              lineHeight: 1.5,
              whiteSpace: "pre-line",
              color: "#212529",
            }}
          >
            {SIMULATION_PRORATA_INFO}
          </div>
        </>
      )}
    </span>
  );
};

const FlotteSimulator = ({ item }) => {
  // État pour stocker les projections
  const { projections, externalValues, updateProjection, updateRmeField } =
    useFlotteSimulator(item);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [expandedRmeIndex, setExpandedRmeIndex] = useState(null);

  // Configuration des colonnes éditables depuis simulatorUtils
  const editableColumns = getEditableColumns();

  // Fonction pour rendre une cellule éditable
  const renderEditableCell = (projection, index, column) => {
    const disabled = ![
      "simulation",
      "simulation_prorata_km",
      "fin_reelle",
      "prolongation",
    ].includes(projection.mouvement);
    if (
      fieldConfigs[column.field]?.disabledMouvements?.includes(
        projection.mouvement,
      )
    ) {
      return "";
    }
    // Pour le mouvement "contrat", afficher montant_vr à la place de valeur_theorique
    let displayValue = projection[column.field];
    if (
      projection.mouvement === "contrat" &&
      column.field === "valeur_theorique"
    ) {
      displayValue = projection.montant_vr;
    }

    // Cellules marquées readOnly : pas d'EditButton, affichage formatté simple.
    if (fieldConfigs[column.field]?.readOnly) {
      return formatDisplayValue(column.field, displayValue);
    }

    return (
      <EditButton
        projectionIndex={index}
        field={column.field}
        value={displayValue}
        disabled={disabled}
        updateProjection={updateProjection}
        externalValues={externalValues}
        calculatedValues={projection.calculatedValues}
      />
    );
  };

  return (
    <div style={{ position: "relative" }}>
      <div className={`${styles.tableContainerSimulator} table-responsive`}>
        <table className="table bg-default">
          <thead className="table-light">
            <tr>
              <th style={{ minWidth: 138, verticalAlign: "bottom" }}>
                Mouvement
              </th>
              {editableColumns.map((column) => (
                <th
                  key={column.field}
                  style={{ ...column.style, verticalAlign: "bottom" }}
                >
                  <div style={{ whiteSpace: "normal", minWidth: 100 }}>
                    {column.title}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {projections.map((projection, index) => {
              const borderColor =
                mouvementBorderColors[projection.mouvement] || "#909090";
              const isHovered = hoveredIndex === index;
              const textColor = `color-mix(in srgb, ${borderColor} 80%, #000)`;
              const isRmeExpanded =
                projection.mouvement === "fin_reelle" &&
                expandedRmeIndex === index;
              return (
                <React.Fragment key={index}>
                  <tr
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    style={{
                      boxShadow: isHovered
                        ? `inset 4px 0 8px -2px ${borderColor}, 0 1px 0 rgba(0,0,0,0.04)`
                        : `inset 4px 0 6px -2px ${borderColor}`,
                      backgroundImage: `linear-gradient(to right, ${borderColor}${isHovered ? "33" : "22"}, transparent 2000px)`,
                      transition:
                        "box-shadow 0.15s ease, background-image 0.15s ease",
                    }}
                  >
                    <td style={{ fontWeight: 600, color: textColor }}>
                      {projection.mouvement === "simulation_prorata_km" ? (
                        <SimulationProrataLabel
                          name={
                            mouvementOptions.find(
                              (option) => option.id === projection.mouvement,
                            )?.name
                          }
                        />
                      ) : (
                        mouvementOptions.find(
                          (option) => option.id === projection.mouvement,
                        )?.name
                      )}
                      {projection.mouvement === "fin_reelle" && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 ml-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedRmeIndex(
                              expandedRmeIndex === index ? null : index,
                            );
                          }}
                          style={{
                            color: textColor,
                            textDecoration: "none",
                            verticalAlign: "baseline",
                          }}
                          title="Détails Frais"
                        >
                          {expandedRmeIndex === index ? "▾" : "▸"} Frais
                        </button>
                      )}
                    </td>
                    {editableColumns.map((column) => (
                      <td key={column.field}>
                        {renderEditableCell(projection, index, column)}
                      </td>
                    ))}
                  </tr>
                  {isRmeExpanded && (
                    <tr>
                      <td
                        colSpan={editableColumns.length + 1}
                        style={{ padding: 0 }}
                      >
                        <FlotteRmeTable
                          projection={projection}
                          index={index}
                          updateRmeField={updateRmeField}
                          externalValues={externalValues}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {projections.length === 0 && (
        <div className="text-center py-3">
          <p className="text-muted">Aucun mouvement trouvé</p>
        </div>
      )}
    </div>
  );
};

export default FlotteSimulator;
